import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Lock, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Admin — internal cost per role in AUD (margin basis). Leadership only. */
export const CostRateCardCard = () => {
  const [rates, setRates] = useState(null);
  const [regional, setRegional] = useState({});
  const [activeRegion, setActiveRegion] = useState('');
  const [entityRegions, setEntityRegions] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/role-pricing/regions`, { withCredentials: true })
      .then(r => setEntityRegions(r.data.regions || [])).catch(() => setEntityRegions([]));
  }, []);
  const tabRegions = Array.from(new Set([...entityRegions, ...Object.keys(regional)])).sort();
  const [minMargin, setMinMargin] = useState('');
  const [roles, setRoles] = useState([]);
  const [allowed, setAllowed] = useState(true);
  const [newRegion, setNewRegion] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/cost-rate-card`, { withCredentials: true });
      setRates(res.data.rates || {});
      setRegional(res.data.regional || {});
      setMinMargin(res.data.min_margin_pct ?? '');
      setRoles(res.data.roles || []);
    } catch (e) {
      if (e.response?.status === 403) setAllowed(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addRegion = () => {
    const key = newRegion.trim().toUpperCase();
    if (!key || regional[key]) return;
    setRegional(prev => ({ ...prev, [key]: {} }));
    setActiveRegion(key);
    setNewRegion('');
  };
  const removeRegion = (region) => {
    setRegional(prev => {
      const next = { ...prev };
      delete next[region];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const cleanRates = {};
      Object.entries(rates).forEach(([role, v]) => {
        const n = parseFloat(v);
        if (Number.isFinite(n) && n > 0) cleanRates[role] = n;
      });
      const cleanRegional = {};
      Object.entries(regional).forEach(([region, rr]) => {
        const rc = {};
        Object.entries(rr || {}).forEach(([role, v]) => {
          const n = parseFloat(v);
          if (Number.isFinite(n) && n > 0) rc[role] = n;
        });
        if (Object.keys(rc).length) cleanRegional[region] = rc;
      });
      const res = await axios.put(`${API_URL}/api/settings/cost-rate-card`,
        { rates: cleanRates, regional: cleanRegional, min_margin_pct: minMargin === '' ? null : minMargin },
        { withCredentials: true });
      toast.success(res.data?.message || 'Internal cost rates saved');
      setRates(res.data.rates || {});
      setRegional(res.data.regional || {});
      setMinMargin(res.data.min_margin_pct ?? '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save cost rates');
    } finally { setBusy(false); }
  };

  if (!allowed) return null;

  return (
    <Card className="border-slate-200" data-testid="cost-rate-card-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-4 h-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Internal Cost Rates (A$/hr)</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Seertech's internal delivery cost per role, in AUD. Drives the leadership-only margin view
          (sell price − cost). Regional overrides apply role-by-role — blank falls back to the global rate.
          Never shown to sales reps or viewers.
        </p>
        {!rates ? (
          <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-1" role="tablist" data-testid="cost-rate-region-tabs">
              {[''].concat(tabRegions).map(region => {
                const n = region ? Object.values(regional[region] || {}).filter(v => v !== '' && v != null).length : Object.values(rates).filter(v => v !== '' && v != null).length;
                const active = activeRegion === region;
                return (
                  <button key={region || '__global'} type="button" role="tab" aria-selected={active}
                    onClick={() => { if (region && regional[region] === undefined) setRegional(prev => ({ ...prev, [region]: {} })); setActiveRegion(region); }}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`cost-rate-region-tab-${region ? region.toLowerCase() : 'global'}`}>
                    {region || 'Global'} <span className="opacity-60">{n}</span>
                  </button>
                );
              })}
            </div>
            {activeRegion === '' && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Global defaults</div>
              <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
                {roles.map(role => (
                  <div key={role}>
                    <div className="text-[10px] font-semibold text-slate-500">{role}</div>
                    <Input value={rates[role] ?? ''} placeholder="—"
                      onChange={e => setRates(prev => ({ ...prev, [role]: e.target.value }))}
                      className="h-7 mt-0.5 text-xs font-mono"
                      data-testid={`cost-rate-${role.toLowerCase()}`} />
                  </div>
                ))}
              </div>
            </div>
            )}
            {activeRegion !== '' && regional[activeRegion] !== undefined && (
              <div className="border border-slate-200 rounded-lg p-3" data-testid={`cost-region-${activeRegion.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#0F2040] tracking-wider">{activeRegion} <span className="font-normal text-slate-400">cost overrides — blank falls back to the global rate; regional margin uses these local delivery costs</span></span>
                  <Button size="sm" variant="ghost" onClick={() => { removeRegion(activeRegion); setActiveRegion(''); }}
                    className="h-6 text-slate-400 hover:text-rose-600" data-testid={`cost-region-remove-${activeRegion.toLowerCase()}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
                  {roles.map(role => (
                    <div key={role}>
                      <div className="text-[10px] font-semibold text-slate-500">{role}
                        <span className="ml-1 font-normal text-slate-400">glob {rates[role] || '—'}</span>
                      </div>
                      <Input value={regional[activeRegion]?.[role] ?? ''} placeholder={String(rates[role] || '—')}
                        onChange={e => setRegional(prev => ({ ...prev, [activeRegion]: { ...prev[activeRegion], [role]: e.target.value } }))}
                        className="h-7 mt-0.5 text-xs font-mono"
                        data-testid={`cost-rate-${activeRegion.toLowerCase()}-${role.toLowerCase()}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Margin guardrail</label>
              <Input value={minMargin} placeholder="e.g. 30"
                onChange={e => setMinMargin(e.target.value)}
                className="h-7 w-20 text-xs font-mono"
                data-testid="min-margin-pct-input" />
              <span className="text-[10px] text-slate-400">% minimum margin — estimates below this are flagged to leadership. Blank disables.</span>
            </div>
            <div className="flex items-center gap-2">
              <Input value={newRegion} onChange={e => setNewRegion(e.target.value.toUpperCase())}
                placeholder="Add region (e.g. APAC)" className="h-7 text-xs font-mono w-44"
                onKeyDown={e => { if (e.key === 'Enter') addRegion(); }}
                data-testid="cost-region-new-input" />
              <Button size="sm" variant="outline" onClick={addRegion} className="h-7" data-testid="cost-region-add-btn">
                <Plus className="w-3 h-3 mr-1" /> Add region
              </Button>
              <div className="flex-1" />
              <Button onClick={save} disabled={busy} size="sm"
                className="bg-[#0F2040] hover:bg-[#1a3156] text-white" data-testid="cost-rate-card-save-btn">
                {busy ? 'Saving…' : 'Save Cost Rates'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CostRateCardCard;
